/**
 * Kalendertage in Europe/Berlin, unabhängig von der Zeitzone des Rechners.
 *
 * Fristen wie Anmeldeschluss oder Vertragsrückgabe werden als Datum gepflegt
 * und meinen den ganzen Tag in deutscher Zeit. Die Cloud Functions laufen in
 * UTC, `setHours(23, 59)` wäre dort erst 01:59 Uhr deutscher Zeit — deshalb
 * rechnen alle Fristvergleiche über diese Helfer (#1320).
 *
 * Doppelt gehalten in functions/src/_shared/berlin-day.utils.ts, geprüft in
 * apps/parity-tests/berlin-day.parity.spec.ts — Änderungen immer in BEIDEN Dateien.
 */

const BERLIN = 'Europe/Berlin';

const BERLIN_DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: BERLIN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const BERLIN_OFFSET_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: BERLIN,
  timeZoneName: 'longOffset',
});

/** Offset von Europe/Berlin zu UTC in Minuten zum Zeitpunkt `instant` (60 oder 120). */
function berlinOffsetMinutes(instant: Date): number {
  const name =
    BERLIN_OFFSET_FORMAT.formatToParts(instant).find((p) => p.type === 'timeZoneName')
      ?.value ?? 'GMT+01:00';
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(name);
  if (!match) return 60;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

/** Zeitpunkt, zu dem `day` (YYYY-MM-DD) in Berlin um 00:00 beginnt. */
function berlinMidnight(day: string): Date {
  const utcMidnight = new Date(`${day}T00:00:00.000Z`);
  // Offset vor der Mitternacht nehmen: Die Umstellung (01:00 UTC) liegt danach, also gilt
  // an Umstellungstagen um 00:00 Berlin noch der alte Offset.
  const offset = berlinOffsetMinutes(new Date(utcMidnight.getTime() - 3 * 3_600_000));
  return new Date(utcMidnight.getTime() - offset * 60_000);
}

/** 00:00:00.000 des Berliner Kalendertags, in den `date` fällt. */
export function startOfBerlinDay(date: Date): Date {
  return berlinMidnight(BERLIN_DAY_FORMAT.format(date));
}

/** 23:59:59.999 des Berliner Kalendertags, in den `date` fällt (Umstellungstage mit 23/25 h inklusive). */
export function endOfBerlinDay(date: Date): Date {
  const next = new Date(startOfBerlinDay(date).getTime() + 36 * 3_600_000);
  return new Date(startOfBerlinDay(next).getTime() - 1);
}

/**
 * Ist `now` nach dem Ende des Berliner Kalendertags von `day`?
 *
 * Ein als Stichtag gepflegter Zeitpunkt (meist Mitternacht) meint den ganzen
 * Tag, nicht nur seine erste Sekunde (#958, #959, #1320).
 */
export function isAfterBerlinDay(now: Date, day: Date): boolean {
  return now.getTime() > endOfBerlinDay(day).getTime();
}
